import { FormEventHandler, useEffect, useRef } from "react";
import styles from "./TemplateInterpreter.module.scss";
import { BasicStep, Field } from "../../types";
import { FormikProps, FormikValues } from "formik";
import { FormRow } from "@allaria-tech/matter/FormRow";
import { Form } from "@allaria-tech/matter/Form";
import TemplateInputSelector from "./TemplateInputSelector";
import useMobileLayout from "../../hooks/useMobileLayout";
import {
  getIsConditionallyTrue,
  getShouldFieldRender,
  groupFieldsIntoRows,
} from "./utils";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { RootState } from "../../app/store";

interface TemplateInterpreterFormProps {
  templateLoaded: boolean;
  onSubmit: FormEventHandler<HTMLFormElement> | undefined;
  currentStepData: BasicStep | undefined;
  formik: FormikProps<FormikValues>;
}

const TemplateInterpreterForm = ({
  onSubmit,
  templateLoaded,
  currentStepData,
  formik,
}: TemplateInterpreterFormProps) => {
  const {
    formContainer,
    formRow,
    sectionTitle: sectionTitleStyle,
    divider: dividerStyle,
    cryptoFullScreenForm,
  } = styles;

  const { values } = formik;

  const dispatch = useAppDispatch();
  const state = useAppSelector((appState: RootState) => appState);

  const { isCryptoFullScreen } = useMobileLayout();

  const formContainerRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      let parent = formContainerRef.current?.parentElement;

      while (parent) {
        const { overflowY } = getComputedStyle(parent);

        if (overflowY === "auto" || overflowY === "scroll") {
          parent.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }

        parent = parent.parentElement;
      }
    }, 100);

    return () => clearTimeout(timeout);
  }, [currentStepData]);

  const renderFieldRows = (fields?: Field[]) => {
    if (!fields) return null;

    const visibleFields = fields.filter((field) =>
      getShouldFieldRender(field, values, dispatch, state),
    );

    return groupFieldsIntoRows(visibleFields).map((row, index) => (
      <FormRow key={index} columns={row.columns} className={formRow}>
        {row.fields.map((field) => (
          <TemplateInputSelector
            currentStepFields={fields}
            key={field.name}
            field={field}
            formik={formik}
          />
        ))}
      </FormRow>
    ));
  };

  return (
    <form onSubmit={onSubmit} ref={formContainerRef}>
      {templateLoaded && (
        <Form
          className={`${formContainer} ${isCryptoFullScreen ? cryptoFullScreenForm : ""}`}
        >
          {renderFieldRows(currentStepData?.fields)}
          {currentStepData?.fieldsSections?.map(
            ({ name, fields, conditional, allConditionalsTrue }) =>
              conditional &&
              !getIsConditionallyTrue(
                values,
                conditional,
                allConditionalsTrue,
              ) ? null : (
                <div key={name}>
                  {currentStepData?.fields && <hr className={dividerStyle} />}
                  <h2 className={sectionTitleStyle}>{name}</h2>
                  <Form className={formContainer}>
                    {renderFieldRows(fields)}
                  </Form>
                </div>
              ),
          )}
        </Form>
      )}
    </form>
  );
};

export default TemplateInterpreterForm;
